import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import collectionRepository from '@/features/collections/repositories/CollectionRepository';
import pageRepository from '@/features/pages/repositories/PageRepository';
import { usePageStore } from '@/features/pages/stores/usePageStore';
import { invalidateCollectionQueries, invalidatePageQueries } from '@/shared/query/queryKeys';

/**
 * Archive/delete/restore mutations for pages.
 *
 * These writes change lists, the archived view, and the open page at once, so
 * they invalidate all page queries; the open page refreshes through its detail
 * query rather than a store copy.
 */
export function usePageArchiveMutations() {
  const queryClient = useQueryClient();
  const setIsDirty = usePageStore(state => state.setIsDirty);

  const deletePage = useCallback(
    async (id: string) => {
      await pageRepository.deletePage(id);
      await invalidatePageQueries(queryClient);
    },
    [queryClient]
  );

  const archivePage = useCallback(
    async (id: string, collectionId?: string) => {
      await pageRepository.archivePage(id);
      setIsDirty(true);

      if (collectionId) {
        void invalidateCollectionQueries(queryClient);
      }
      await invalidatePageQueries(queryClient);
    },
    [queryClient, setIsDirty]
  );

  const unarchivePage = useCallback(
    async (id: string) => {
      await pageRepository.unarchivePage(id);
      const page = await pageRepository.getPageById(id);

      if (!page) return;
      setIsDirty(true);

      if (!page.collectionId) {
        await invalidatePageQueries(queryClient);
        return;
      }

      // Restoring a collection page also restores the parent collection and
      // section, but only this page. Full collection restore remains a separate
      // collection-level action.
      const collection = await collectionRepository.getCollectionById(page.collectionId);
      if (!collection) {
        await invalidatePageQueries(queryClient);
        return;
      }

      const collectionSections = (collection.sections || []).map(section => {
        if (section.id === page.sectionId && section.archivedAt) {
          return { ...section, archivedAt: undefined };
        }
        return section;
      });

      await collectionRepository.updateCollection(page.collectionId, {
        archivedAt: collection.archivedAt ? 0 : collection.archivedAt,
        sections: collectionSections,
      });

      void invalidateCollectionQueries(queryClient);
      await invalidatePageQueries(queryClient);
    },
    [queryClient, setIsDirty]
  );

  return {
    archivePage,
    deletePage,
    unarchivePage,
  };
}
